import React, { useEffect } from 'react';
import KPICard, { KPIGrid } from '../KPICard';
import DataTable from '../DataTable';
import { navigateToWell } from '../../utils/wellIdentity';
import { formatCurrency } from '../../utils/format';
import { riskNext } from '../../utils/riskDisplay';
import { EvidenceLogs } from './Geoscience';
import { track } from '../../lib/track';

export default function Diligence({ data, selectionContext }) {
  const {
    risks,
    vdrStatus,
    checklist,
    valuation,
    contacts,
    evidenceRoom,
    midstreamAssignability,
    logEvidence,
    lasFlags,
    bidProcess,
  } = data;
  const flagWells = lasFlags?.wells || [];
  const process = bidProcess || data.meta?.bidProcess;
  const selectedConcern = selectionContext?.concernId || '';

  useEffect(() => {
    if (!selectedConcern) return;
    const el = document.getElementById(`concern-${selectedConcern}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectedConcern]);

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">VDR status &amp; contracts</h2>
          <p className="page-subtitle">
            Process gaps · P0 checklist · bid/IRR + RedOaks bid letter live on Decision desk
            (multiples = back-check)
          </p>
          <div className="page-quote">
            <span className="page-quote-label">{data.quoteLabel}</span>
            <span>&ldquo;{data.quote}&rdquo;</span>
          </div>
        </div>
      </div>

      {process && (
        <section className="dashboard-section">
          <div className="section-title">Bid instructions (live)</div>
          <div className="chart-card">
            <p className="chart-subtitle" style={{ marginBottom: 10 }}>
              {process.packageName} · {process.formatNote}
            </p>
            <KPIGrid>
              <KPICard
                label="Bids due"
                value={process.bidsDue || '—'}
                sub={process.bidDueLabel || 'noon CT'}
                color="var(--red)"
              />
              <KPICard
                label="Effective date"
                value={process.effectiveDate || '—'}
                sub="Per Bid Instructions §I"
                color="var(--navy)"
              />
              <KPICard
                label="Proposal valid through"
                value={process.proposalValidThrough || '—'}
                sub="Per Bid Instructions §III"
                color="var(--amber)"
              />
              <KPICard
                label="Submit to"
                value={process.submitTo?.name || '—'}
                sub={process.submitTo?.email || ''}
                color="var(--green)"
              />
            </KPIGrid>
            <p className="engine-note" style={{ marginTop: 8 }}>
              Export the formal bid letter from the{' '}
              <a href="#decision" className="linkish">
                Decision
              </a>{' '}
              desk (financing, contingencies, contacts). Form PSA remains a separate P0 if still
              missing from the VDR.
            </p>
          </div>
        </section>
      )}

      <KPIGrid>
        <KPICard
          label="PDP PV10"
          value={formatCurrency(valuation.pdpPv10MM * 1e6)}
          sub={`PV15 ${formatCurrency(valuation.pv15MM * 1e6)} · PV20 ${formatCurrency(valuation.pv20MM * 1e6)}`}
          color="var(--amber)"
        />
        <KPICard
          label="NTM / L12M CF"
          value={`${formatCurrency(valuation.ntmCfMM * 1e6)} / ${formatCurrency(valuation.l12NocfMM * 1e6)}`}
          sub="Seller NTM vs trailing NOCF"
          color="var(--green)"
        />
        <KPICard
          label="Multiple back-check"
          value={`${formatCurrency((valuation.multipleBackcheckLowMM ?? valuation.illustrativeLowMM) * 1e6)}–${formatCurrency((valuation.multipleBackcheckHighMM ?? valuation.illustrativeHighMM) * 1e6)}`}
          sub="4–6× live NTM · not the bid frame"
          color="var(--blue)"
        />
        <KPICard
          label="Undisc. PDP CF"
          value={formatCurrency(valuation.undiscPdpMM * 1e6)}
          sub="Long-life back-end"
          color="var(--navy)"
        />
      </KPIGrid>

      <section className="dashboard-section">
        <div className="section-title">Priority risks</div>
        <div className="charts-grid charts-grid-3">
          {risks.map((r) => (
            <div
              key={r.id}
              id={`concern-${r.id}`}
              className={`risk-card ${r.severity.toLowerCase()}${
                selectedConcern && selectedConcern === r.id ? ' risk-card-selected' : ''
              }`}
            >
              <div className="risk-head">
                <div className="risk-title">{r.title}</div>
                <span className={`pill severity-${r.severity.toLowerCase()}`}>{r.severity}</span>
              </div>
              <p className="risk-summary">{r.summary}</p>
              <p className="risk-fix">
                <strong>Next:</strong> {riskNext(r)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-title">Diligence checklist</div>
        <DataTable
          columns={[
            { key: 'priority', label: 'Pri' },
            { key: 'item', label: 'Item' },
            { key: 'owner', label: 'Owner' },
          ]}
          data={checklist}
          defaultSort="priority"
          defaultDir="asc"
          onRowClick={(row) =>
            track('diligence_item_checked', {
              priority: row.priority,
              item: String(row.item || '').slice(0, 120),
              owner: row.owner || undefined,
            })
          }
        />
      </section>

      {midstreamAssignability?.contracts?.length > 0 && (
        <section className="dashboard-section">
          <div className="section-title">
            Midstream assignability register
            <span className="section-meta">
              {' '}
              · {midstreamAssignability.overallStatus.toUpperCase()} ·{' '}
              {midstreamAssignability.contracts.filter((c) => c.status === 'Open').length} open
            </span>
          </div>
          <p className="chart-subtitle" style={{ marginBottom: 12 }}>
            {midstreamAssignability.overallNote}
          </p>
          <DataTable
            columns={[
              { key: 'id', label: 'ID' },
              { key: 'counterparty', label: 'Counterparty' },
              { key: 'instrument', label: 'Instrument' },
              { key: 'status', label: 'Status' },
              { key: 'assignability', label: 'Assignability' },
              { key: 'bidImpact', label: 'Bid impact' },
              { key: 'owner', label: 'Owner' },
              { key: 'evidence', label: 'Evidence' },
            ]}
            data={midstreamAssignability.contracts}
            defaultSort="id"
            defaultDir="asc"
          />
        </section>
      )}

      <section className="dashboard-section">
        <div className="section-title">VDR completeness</div>
        <DataTable
          columns={[
            { key: 'section', label: 'Section' },
            { key: 'status', label: 'Status' },
            { key: 'note', label: 'Notes' },
          ]}
          data={vdrStatus}
          defaultSort="status"
        />
      </section>

      {evidenceRoom?.wells?.length > 0 && (
        <section className="dashboard-section">
          <div className="section-title">
            Well-file evidence room
            <span className="section-meta">
              {' '}
              · {evidenceRoom.wellCount} wells · {evidenceRoom.fileCount} files · {evidenceRoom.source}
            </span>
          </div>
          <p className="chart-subtitle" style={{ marginBottom: 12 }}>
            Indexed from VDR 6.3 — use for Boswell / Devon Fee 7 P0 file pulls. Paths stay in the Dropbox
            room; this table is the triage index.
          </p>
          <DataTable
            columns={[
              { key: 'priority', label: 'Pri' },
              { key: 'well', label: 'Well' },
              { key: 'field', label: 'Field' },
              { key: 'files', label: 'Files' },
              { key: 'completion', label: 'Compl' },
              { key: 'geology', label: 'Geol' },
              { key: 'ops', label: 'Ops' },
              { key: 'land', label: 'Land' },
              { key: 'rrc', label: 'RRC' },
              { key: 'tags', label: 'Tags' },
            ]}
            data={evidenceRoom.wells}
            defaultSort="priority"
            defaultDir="asc"
            onRowClick={(row) => navigateToWell({ lease: row.well })}
          />
        </section>
      )}

      {flagWells.length > 0 && (
        <section className="dashboard-section">
          <div className="section-title">
            Secondary LAS pay / CBL flags
            <span className="section-meta">
              {' '}
              · {lasFlags.possiblePayFileCount ?? 0} possible-pay · {lasFlags.cblLasCount ?? 0} CBL ·{' '}
              <span className="pill severity-low">Diligence only — not in PV</span>
            </span>
          </div>
          <p className="chart-subtitle" style={{ marginBottom: 12 }}>
            Rule-based GR / porosity / resistivity screen on Bald Prairie secondaries. Evidence only —
            does not change Decision PV or the Boswell volume dial. Open curves in Geoscience.
          </p>
          <DataTable
            columns={[
              { key: 'well', label: 'Well' },
              { key: 'lasCount', label: 'LAS' },
              { key: 'possiblePayFiles', label: 'Possible pay' },
              {
                key: 'netPayFt',
                label: 'Net pay (screen)',
                format: (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `${Number(v).toFixed(0)} ft`),
              },
              { key: 'hasCblLas', label: 'CBL', format: (v) => (v ? 'Yes' : '—') },
            ]}
            data={flagWells}
            defaultSort="possiblePayFiles"
            defaultDir="desc"
          />
        </section>
      )}

      <EvidenceLogs logEvidence={logEvidence} dashboardOrigin="diligence" />

      <section className="dashboard-section">
        <div className="section-title">RedOaks contacts</div>
        <DataTable
          columns={[
            { key: 'name', label: 'Name' },
            { key: 'role', label: 'Role' },
            { key: 'email', label: 'Email' },
          ]}
          data={contacts}
          defaultSort="name"
          defaultDir="asc"
        />
        <p className="chart-subtitle" style={{ marginTop: 12 }}>
          {valuation.note}
        </p>
      </section>
    </div>
  );
}
